import { navigationItems } from './navigation';
import { posts } from './../data/posts';

type WebMcpTool = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<{ content: Array<{ type: 'text'; text: string }> }>;
  annotations?: Record<string, unknown>;
};

type ModelContext = {
  registerTool?: (tool: WebMcpTool, options?: { signal?: AbortSignal }) => unknown;
  provideContext?: (context: { tools: WebMcpTool[] }) => unknown;
};

const SITE = 'https://pharmakademie.com';

const calculators = [
  {
    id: 'brutto-netto',
    name: 'Brutto-Netto-Rechner',
    description: 'Berechnet das Netto-Gehalt von Apothekern inklusive Steuern und Sozialabgaben.',
    path: '/brutto-netto-rechner',
  },
  {
    id: 'krankenversicherung',
    name: 'Krankenversicherungsrechner',
    description: 'Vergleicht Beiträge zur gesetzlichen Krankenversicherung nach Kasse und Einkommen.',
    path: '/versicherungen/krankenversicherung',
  },
  {
    id: 'altersvorsorgedepot',
    name: 'Altersvorsorgedepot-Rechner',
    description: 'Simuliert Förderung und Endkapital des Altersvorsorgedepots ab 2027.',
    path: '/altersvorsorge/altersvorsorgedepot',
  },
  {
    id: 'basisrente',
    name: 'Basisrenten-Rechner',
    description: 'Berechnet Steuervorteil und Rentenhöhe einer Basisrente.',
    path: '/altersvorsorge/basis',
  },
];

function flattenNav(): Array<{ label: string; url: string }> {
  const out: Array<{ label: string; url: string }> = [];
  for (const item of navigationItems) {
    out.push({ label: item.label, url: SITE + item.href });
    for (const child of item.children ?? []) {
      out.push({ label: `${item.label} – ${child.label}`, url: SITE + child.href });
    }
  }
  return out;
}

function text(payload: unknown) {
  return { content: [{ type: 'text' as const, text: JSON.stringify(payload, null, 2) }] };
}

function buildTools(): WebMcpTool[] {
  return [
    {
      name: 'search_articles',
      description:
        'Durchsucht die Beiträge (Blogartikel) von Pharmakademie nach Titel, Teaser und Rubrik und liefert Titel, Datum und URL.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Suchbegriff, z. B. "ETF" oder "Versorgungswerk".' },
          limit: { type: 'number', description: 'Maximale Anzahl der Ergebnisse (Standard 5).' },
        },
        required: ['query'],
      },
      annotations: { readOnlyHint: true, openWorldHint: false },
      execute: async ({ query, limit }) => {
        const q = String(query ?? '').toLowerCase().trim();
        const max = typeof limit === 'number' && limit > 0 ? Math.min(limit, 20) : 5;
        const results = posts
          .filter((p) =>
            [p.title, p.excerpt, p.kicker].some((f) => f.toLowerCase().includes(q))
          )
          .slice(0, max)
          .map((p) => ({
            title: p.title,
            kicker: p.kicker,
            excerpt: p.excerpt,
            date: p.dateISO,
            url: `${SITE}/beitraege/${p.slug}`,
          }));
        return text({ query: q, count: results.length, results });
      },
    },
    {
      name: 'list_calculators',
      description: 'Listet alle kostenfreien Finanzrechner für Apotheker mit Beschreibung und URL.',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true, openWorldHint: false },
      execute: async () =>
        text(calculators.map((c) => ({ ...c, url: SITE + c.path }))),
    },
    {
      name: 'list_topics',
      description:
        'Liefert die Themenstruktur der Website (Versicherungen, Altersvorsorge, Investment, Beratung) mit URLs.',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true, openWorldHint: false },
      execute: async () => text(flattenNav()),
    },
    {
      name: 'navigate',
      description:
        'Öffnet eine Seite dieser Website im Browser. Erwartet einen relativen Pfad wie "/beitraege" oder "/brutto-netto-rechner".',
      inputSchema: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'Relativer Pfad auf pharmakademie.com, beginnend mit "/".' },
        },
        required: ['path'],
      },
      annotations: { readOnlyHint: false, openWorldHint: false },
      execute: async ({ path }) => {
        const raw = String(path ?? '');
        if (!raw.startsWith('/') || raw.startsWith('//')) {
          return text({ ok: false, error: 'Ungültiger Pfad. Erwartet wird ein relativer Pfad wie "/beitraege".' });
        }
        window.location.assign(raw);
        return text({ ok: true, navigatedTo: SITE + raw });
      },
    },
    {
      name: 'get_consultation_info',
      description:
        'Gibt Informationen und Buchungslinks für eine kostenfreie individuelle Finanzplanung oder ein Online-Seminar zurück.',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true, openWorldHint: false },
      execute: async () =>
        text({
          individuelleBeratung: `${SITE}/beratung/individuell`,
          onlineSeminare: `${SITE}/beratung/seminare`,
          zielgruppe: 'Approbierte Apothekerinnen und Apotheker',
          kosten: 'kostenfrei',
        }),
    },
  ];
}

let controller: AbortController | null = null;

export function registerWebMcpTools(): () => void {
  const modelContext = (navigator as Navigator & { modelContext?: ModelContext }).modelContext;
  if (!modelContext) return () => {};

  controller?.abort();
  controller = new AbortController();
  const { signal } = controller;
  const tools = buildTools();

  try {
    if (typeof modelContext.registerTool === 'function') {
      for (const tool of tools) {
        modelContext.registerTool(tool, { signal });
      }
    } else if (typeof modelContext.provideContext === 'function') {
      modelContext.provideContext({ tools });
    }
  } catch {
    // WebMCP nicht verfügbar oder abweichende Implementierung: still ignorieren.
  }

  return () => controller?.abort();
}
